import { newsPosts } from "../content/news";
import type { NewsPost } from "../content/types";

// Reads the articles in src/content/news.ts. Pages use these helpers, never the raw list,
// so drafts and future-dated posts can't leak onto the live site.

/** The development server (npm start) previews drafts and scheduled posts; production builds never show them */
export const SHOW_UNPUBLISHED = process.env.NODE_ENV === "development";

/** Today in South Africa as YYYY-MM-DD, the format publishedOn uses */
const todaySast = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

/** Published and its date has arrived */
export const isLive = (post: NewsPost) => post.status === "published" && post.publishedOn <= todaySast();

const isVisible = (post: NewsPost) => isLive(post) || SHOW_UNPUBLISHED;

/** Whether any article is live; until one is, the news areas show a "coming soon" card */
export const hasLivePosts = () => newsPosts.some(isLive);

/** Newest first */
export const listPosts = (): NewsPost[] =>
  newsPosts.filter(isVisible).sort((a, b) => b.publishedOn.localeCompare(a.publishedOn) || a.title.localeCompare(b.title));

export const getPost = (slug: string) => newsPosts.find(post => post.slug === slug && isVisible(post));

/** Articles that link to a webinar, for its "Related reading" list */
export const postsForWebinar = (webinarSlug: string) =>
  listPosts().filter(post => post.relatedWebinars?.includes(webinarSlug));

/** "2026-10-14" → "14 October 2026" */
export const formatPostDate = (ymd: string) =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

/** Draft or scheduled: shown only on the development server, with this label */
export const unpublishedLabel = (post: NewsPost) =>
  post.status === "draft" ? "Draft" : !isLive(post) ? `Scheduled: ${formatPostDate(post.publishedOn)}` : null;

// ══════════════════════════════════════════════════════════════════════════════
//   ARTICLE TEXT: plain text with [link text](address) links
// ══════════════════════════════════════════════════════════════════════════════

export type TextSegment = { text: string } | { text: string; href: string; internal: boolean };

const LINK = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;
const SAFE_HREF = /^(https?:\/\/|mailto:|\/(?!\/))/i;

/**
 * Splits article text into plain text and links. Only safe addresses become links, so a
 * typo like [x](javascript:...) stays visible as text instead of becoming a script link.
 */
export function parseArticleText(source: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let last = 0;
  for (const match of Array.from(source.matchAll(LINK))) {
    const [whole, label, href] = match;
    const start = match.index ?? 0;
    if (!SAFE_HREF.test(href)) continue;
    if (start > last) segments.push({ text: source.slice(last, start) });
    segments.push({ text: label, href, internal: href.startsWith("/") });
    last = start + whole.length;
  }
  if (last < source.length) segments.push({ text: source.slice(last) });
  return segments;
}

// Catch content mistakes while editing, in the browser console of the development server
if (SHOW_UNPUBLISHED) {
  const seen = new Set<string>();
  for (const post of newsPosts) {
    const problems = [
      seen.has(post.slug) && "its slug is used by another post",
      !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(post.slug) && "its slug must be lower-case words joined by hyphens",
      !/^\d{4}-\d{2}-\d{2}$/.test(post.publishedOn) && "publishedOn must look like 2026-10-14",
      post.imageUrl && !post.imageAlt && "imageAlt is required when imageUrl is set",
      post.imageUrl && !/^https:\/\//.test(post.imageUrl) && "imageUrl must start with https://",
    ].filter(Boolean);
    if (problems.length) console.warn(`News post "${post.slug}": ${problems.join("; ")}.`);
    seen.add(post.slug);
  }
}
